import { ExpandOutlined, PauseCircleFilled, PlayCircleFilled } from "@ant-design/icons";
import ProfileContainer from "../ProctoredMedia/ProfileContainer";
import { capitalizeTitle, roundOff } from "src/utils/helperFunctions";
import { clockOf, span } from "./rules/format";
import { LEAD } from "./rules/proctoring";
import { useReviewClock } from "./clock";
import Shot from "./Shot";

/*
 * What a flag rests on, under the item in the queue: the capture and the ones
 * around it, the profile check, the screen snapshot, the audio (played from the
 * audio recording) and the transcript lines at that moment, the answer
 * integrity figures, or the tab count. Everything shown is captured data.
 */

const STATUS_WORD = { cheating: "Cheating", maybeCheating: "Possible cheating", notCheating: "Clear" };
const pct = (v) => (v == null || !Number.isFinite(Number(v)) ? null : Math.round(Number(v) * 100));

function Verdict({ verdict, criticalReason, note }) {
  if (!verdict && !criticalReason && !note) return null;
  return (
    <div className="proctoring-review__ev-verdict">
      {verdict && verdict.status && (
        <p className={`proctoring-review__ev-status proctoring-review__ev-status--${verdict.status}`}>
          {STATUS_WORD[verdict.status] || capitalizeTitle(verdict.status)}
          {verdict.type ? <span>{` · ${capitalizeTitle(verdict.type)}`}</span> : null}
        </p>
      )}
      {verdict && verdict.remark && <p className="proctoring-review__ev-remark">{verdict.remark}</p>}
      {(!verdict || !verdict.remark) && criticalReason && <p className="proctoring-review__ev-remark">{String(criticalReason)}</p>}
      {note && <p className="proctoring-review__ev-note">{note}</p>}
    </div>
  );
}

/** Professional readiness as the image AI scored it for this capture (shown when the position turns it on). */
export function Readiness({ readiness }) {
  if (!readiness) return null;
  const row = (label, part) => (part ? (
    <p key={label}>
      <b>{`${label}: ${roundOff(part.score ?? part.averageScore)}`}</b>
      {part.remark || part.summary ? ` (${part.remark || part.summary})` : ""}
    </p>
  ) : null);
  return (
    <div className="proctoring-review__ev-readiness">
      <p className="proctoring-review__ev-readiness-title">
        Professional readiness
        {readiness.overallScore != null && <b>{` ${roundOff(readiness.overallScore)}`}</b>}
      </p>
      {(readiness.overallSummary || readiness.summary) && <p>{readiness.overallSummary || readiness.summary}</p>}
      {row("Facial expressions", readiness.facialExpressions)}
      {row("Eye contact", readiness.eyeContact)}
      {row("Grooming", readiness.grooming)}
      {row("Professional conduct", readiness.professionalConduct)}
    </div>
  );
}

function Meter({ label, value, hint }) {
  const p = pct(value);
  if (p == null) return null;
  return (
    <div className="proctoring-review__meter" title={hint}>
      <span className="proctoring-review__meter-fill" style={{ width: `${p}%` }} />
      <b>{`${label}: ${p} in 100`}</b>
    </div>
  );
}

/**
 * The transcript lines around a moment; the ones inside it, and the line a quote was found on (`line`), are
 * marked. A line seeks the clock.
 */
function TranscriptLines({ lines, from, to, line }) {
  const c = useReviewClock();
  const near = (lines || []).filter((l) => l && Number.isFinite(l.from)
    && ((line && l.line === line) || (l.to >= from - 3 && l.from <= to + 3)));
  if (!near.length) return <p className="proctoring-review__ev-muted">No transcript lines at this moment.</p>;
  return (
    <ol className="proctoring-review__ev-lines" aria-label="Transcript at this moment">
      {near.map((l) => (
        <li key={l.line || `${l.from}-${l.text}`} className={(line && l.line === line) || (!line && l.to >= from && l.from <= to) ? "is-hit" : ""}>
          <button type="button" onClick={() => c.seek(l.from)} title="Go to this line">
            <span>{clockOf(l.from)}</span>
            {l.text}
          </button>
        </li>
      ))}
    </ol>
  );
}

/** Speech-detection stretches this far either side of an audio item are drawn around it. */
export const SPEECH_CONTEXT = 15;

/**
 * The stretches of detected speech around an audio item, as blocks on a short line (not a waveform: speech
 * detection keeps when someone spoke, not the sound). Blocks that overlap the item are marked; a block seeks
 * the clock to its start.
 * @param {Array<{at: number, dur: number}>} speech  timeline.audio.speech
 * @param {number} from  the item's start
 * @param {number} to    its end
 * @param {number} length  the round's length
 * @returns {{lo: number, hi: number, blocks: Array<{at: number, dur: number, left: number, width: number, hit: boolean}>}}
 */
export function speechAround(speech, from, to, length) {
  const lo = Math.max(0, from - SPEECH_CONTEXT);
  const hi = Math.min(Math.max(length || 0, to), to + SPEECH_CONTEXT);
  const W = Math.max(1, hi - lo);
  const blocks = (speech || [])
    .filter((x) => x && Number.isFinite(x.at) && Number(x.dur) > 0 && x.at < hi && x.at + Number(x.dur) > lo)
    .map((x) => {
      const a = Math.max(lo, x.at);
      const b = Math.min(hi, x.at + Number(x.dur));
      return {
        at: x.at,
        dur: Number(x.dur),
        left: ((a - lo) / W) * 100,
        width: Math.max(0.6, ((b - a) / W) * 100),
        hit: x.at < to && x.at + Number(x.dur) > from,
      };
    });
  return { lo, hi, blocks };
}

function SpeechStrip({ speech, from, to, floorOf }) {
  const c = useReviewClock();
  const { lo, hi, blocks } = speechAround(speech, from, to, c.length);
  if (!blocks.length) return null;
  const W = Math.max(1, hi - lo);
  return (
    <div className="proctoring-review__ev-speech" aria-label="Speech detected around this moment">
      <div className="proctoring-review__ev-speech-line">
        <span className="proctoring-review__ev-speech-item" style={{ left: `${((from - lo) / W) * 100}%`, width: `${Math.max(0.6, ((Math.max(to, from + 0.5) - from) / W) * 100)}%` }} />
        {blocks.map((b) => (
          <button
            key={`${b.at}-${b.dur}`}
            type="button"
            className={`proctoring-review__ev-speech-block${b.hit ? " is-hit" : ""}`}
            style={{ left: `${b.left}%`, width: `${b.width}%` }}
            onClick={() => c.seek(b.at, { floor: floorOf ? floorOf(b.at) : null })}
            title={`Speech ${clockOf(b.at)} for ${span(b.dur)}`}
            aria-label={`Speech at ${clockOf(b.at)} for ${span(b.dur)}`}
          />
        ))}
      </div>
      <div className="proctoring-review__ev-speech-ends" aria-hidden="true">
        <span>{clockOf(lo)}</span>
        <span>Speech detected</span>
        <span>{clockOf(hi)}</span>
      </div>
    </div>
  );
}

function PlaySegment({ from, to, label }) {
  const c = useReviewClock();
  const on = c.playing && c.t >= from - 0.5 && c.t <= to + 0.5;
  return (
    <button
      type="button"
      className={`proctoring-review__ev-play${on ? " is-on" : ""}`}
      onClick={() => (on ? c.pause() : c.playSegment(from, to))}
      disabled={!c.hasDriver && !c.length}
    >
      {on ? <PauseCircleFilled /> : <PlayCircleFilled />}
      {on ? "Pause" : label || `Play ${clockOf(from)} to ${clockOf(to)}`}
    </button>
  );
}

/**
 * @param {Object} props
 * @param {Object} props.x            the FeedItem
 * @param {Object} [props.src]        its entry in the timeline (capture, screen, profile, marker, instance, integrity)
 * @param {Array}  props.captures     every capture item, for the neighbours
 * @param {Function} props.imageOf    (item) => url
 * @param {Function} props.onImageError
 * @param {Function} props.onPick
 * @param {Function} props.onZoom
 * @param {Object} [props.audio]      timeline.audio: transcript and review
 * @param {Object} [props.question]   the timeline question the item belongs to
 * @param {boolean} props.hasAudio    an audio recording can be played
 * @param {boolean} props.showReadiness
 * @param {Function} [props.floorOf]  (at) => where the recording holding `at` starts, or null: a lead-up stops there
 */
export default function Evidence({ x, src, captures, imageOf, onImageError, onPick, onZoom, audio, question, hasAudio, showReadiness, floorOf }) {
  const s = src || {};
  const transcript = (audio && audio.transcript) || [];

  if (x.kind === "capture") {
    const real = captures.filter((y) => y.capture !== "reference");
    const i = real.findIndex((y) => y.key === x.key);
    const near = i < 0 ? [] : real.slice(Math.max(0, i - 2), i + 3);
    return (
      <div className="proctoring-review__ev-capture-block">
        <div className="proctoring-review__ev-capture">
          <button type="button" className={`proctoring-review__ev-big${x.flagged ? ` proctoring-review__shot--${x.severity}` : ""}`} onClick={() => onZoom(x.key)} aria-label="Enlarge the capture">
            <Shot url={imageOf(x)} alt={x.what} onError={onImageError} />
            <span className="proctoring-review__ev-zoom"><ExpandOutlined /> Enlarge</span>
          </button>
          <div className="proctoring-review__ev-side">
            {x.capture === "reference"
              ? <p className="proctoring-review__ev-muted">{x.detail}</p>
              : <Verdict verdict={s.verdict} criticalReason={s.criticalReason} note={x.note} />}
            {showReadiness && <Readiness readiness={s.professionalReadiness} />}
          </div>
        </div>
        {near.length > 1 && (
          <div className="proctoring-review__ev-near">
            <span>Around it</span>
            <div>
              {near.map((y) => (
                <button
                  key={y.key}
                  type="button"
                  className={`proctoring-review__ev-shot${y.key === x.key ? " is-on" : ""}${y.flagged ? ` proctoring-review__shot--${y.severity}` : ""}`}
                  onClick={() => (y.key === x.key ? onZoom(y.key) : onPick(y))}
                  title={`${clockOf(y.at)} ${y.what}`}
                >
                  <Shot url={imageOf(y)} alt="" onError={onImageError} />
                  <span>{clockOf(y.at)}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (x.kind === "profile") {
    return (
      <div className="proctoring-review__ev-profile">
        <ProfileContainer
          loading={false}
          sessionProfile={{ profileLink: s.profileUrl || null, sessionLink: s.sessionUrl || null, isCritical: s.isCritical, criticalReason: s.criticalReason }}
        />
        {x.detail && <p className="proctoring-review__ev-remark">{x.detail}</p>}
      </div>
    );
  }

  if (x.kind === "screen") {
    return (
      <div className="proctoring-review__ev-capture">
        <button type="button" className={`proctoring-review__ev-big proctoring-review__ev-big--screen${x.flagged ? ` proctoring-review__shot--${x.severity}` : ""}`} onClick={() => onZoom(x.key)} aria-label="Enlarge the screen snapshot">
          <Shot url={imageOf(x)} alt={x.what} onError={onImageError} />
          <span className="proctoring-review__ev-zoom"><ExpandOutlined /> Enlarge</span>
        </button>
        <div className="proctoring-review__ev-side">
          <Verdict verdict={s.verdict || (s.status ? { status: s.status } : null)} criticalReason={s.criticalReason} note={x.note} />
        </div>
      </div>
    );
  }

  if (x.kind === "audio" || x.kind === "quote") {
    const dur = Number(x.dur) || (x.kind === "quote" ? 4 : 0);
    const floor = floorOf ? floorOf(x.at) : null;
    const from = Math.max(Number.isFinite(floor) && floor <= x.at ? floor : 0, x.at - LEAD);
    const to = x.at + Math.max(dur, 1) + 1;
    const review = audio && audio.review;
    return (
      <div className="proctoring-review__ev-audio">
        {x.kind === "quote" && (
          <blockquote className={`proctoring-review__ev-quote proctoring-review__ev-quote--${x.severity}`}>
            <p>{`“${x.what}”`}</p>
            <footer>
              {x.detail && <span>{x.detail}</span>}
              {s.severity && <span className="proctoring-review__chip">{`${s.severity} severity`}</span>}
              <span className="proctoring-review__chip proctoring-review__chip--ai">
                {`AI review${review && review.confidence ? ` · ${review.confidence} confidence` : ""}`}
              </span>
            </footer>
          </blockquote>
        )}
        {x.kind === "audio" && (
          <p className="proctoring-review__ev-remark">
            {`Speech was heard for ${span(dur)} while no spoken answer was expected${question ? ` (${question.label})` : ""}. `}
            <span className="proctoring-review__ev-muted">Speech detection hears that someone spoke, not who.</span>
          </p>
        )}
        {x.approx && <p className="proctoring-review__ev-muted">{`Placed approximately${s.placement === "question" ? ", at the start of its question" : ""}.`}</p>}
        <SpeechStrip speech={audio && audio.speech} from={x.at} to={x.at + Math.max(dur, 0.5)} floorOf={floorOf} />
        {hasAudio
          ? <PlaySegment from={from} to={to} label={`Play ${clockOf(from)} to ${clockOf(to)}`} />
          : <p className="proctoring-review__ev-muted">No audio recording to play.</p>}
        <TranscriptLines lines={transcript} from={x.at} to={x.at + Math.max(dur, 1)} line={x.kind === "quote" ? s.line : null} />
      </div>
    );
  }

  if (x.kind === "integrity") {
    const spoken = s.modality === "spoken";
    return (
      <div className="proctoring-review__ev-integrity">
        <p className="proctoring-review__ev-muted">
          <span className="proctoring-review__badge proctoring-review__badge--experimental">Experimental</span>
          {` ${spoken ? "A spoken" : "A written"} answer${question ? ` to ${question.label}` : ""}, scored by a model. Not counted in the integrity figure; never more than worth a look.`}
        </p>
        <Meter label="Likely AI-written" value={s.aiGenerated} />
        {spoken && <Meter label="Likely read aloud" value={s.readAloud} />}
        <Meter label="Thought through" value={s.thoughtThrough} />
        <div className="proctoring-review__ev-figures">
          {pct(s.confidence) != null && <span>{`Model confidence ${pct(s.confidence)} in 100`}</span>}
          {s.readingScore != null && <span>{`Reading score ${roundOff(s.readingScore)}`}</span>}
          {s.pasteLikeScore != null && <span>{`Paste-like score ${roundOff(s.pasteLikeScore)}`}</span>}
        </div>
        {s.reasons && <p className="proctoring-review__ev-remark">{s.reasons}</p>}
        {Array.isArray(s.markers) && s.markers.length > 0 && (
          <ul className="proctoring-review__ev-markers">
            {s.markers.map((m) => <li key={String(m)}>{String(m)}</li>)}
          </ul>
        )}
      </div>
    );
  }

  if (x.kind === "session") {
    return (
      <div className="proctoring-review__ev-tabs">
        {x.reference && (
          <div className="proctoring-review__ev-capture">
            <div className="proctoring-review__ev-big">
              <Shot url={imageOf({ key: x.reference })} alt="The reference photo retaken for this session" onError={onImageError} />
            </div>
            <div className="proctoring-review__ev-side">
              <p className="proctoring-review__ev-muted">The reference photo retaken for this session; its captures are checked against it, not the first one.</p>
            </div>
          </div>
        )}
        {x.detail && <p className="proctoring-review__ev-remark">{x.detail}</p>}
        <p className="proctoring-review__ev-muted">
          A session begins each time the candidate enters the assessment. A second one means they left and came back, by reloading or reopening the link, and the recording and the reference photo began again.
        </p>
      </div>
    );
  }

  if (x.kind === "tabcount") {
    return (
      <div className="proctoring-review__ev-tabs">
        <p className="proctoring-review__ev-remark">{x.what}.</p>
        <p className="proctoring-review__ev-muted">
          {`Exact times not captured: only a count per question is kept, so this is placed at the start of ${question ? question.label : "its question"}${question && Number.isFinite(question.to) ? ` (${clockOf(question.from)} to ${clockOf(question.to)})` : ""}.`}
        </p>
      </div>
    );
  }

  return x.detail ? <p className="proctoring-review__ev-remark">{x.detail}</p> : null;
}
